// Shared async-design-job flow used by BOTH the DesignPage map callers (seed
// drag-relocate, edit-mode re-run, resume-on-mount) so the complete/failed
// handling is written once, not duplicated per caller. The DesignPanel uses
// watchDesignJob directly (it owns its own progress UI); these helpers wrap the
// same primitives with a normalized lifecycle callback for the map page.
//
// Lifecycle object passed to cb (one of):
//   { status:'already_running', job_id }  // runAndWatch only, BEFORE any watch
//                                         // update: THIS request was not enqueued
//   { status:'queued'|'running', stage, stage_detail, progress_done, progress_total }
//   { status:'complete', result:{elements,routes,summary} }   // geometry loaded
//   { status:'failed',   error }
//   { status:'cancelled' }
import { runDesign, watchDesignJob, getActiveDesignJob, loadProject } from './designApi';

const TERMINAL = ['complete', 'failed', 'cancelled'];

// Pure predicate: should the resume-on-mount path re-attach to this job? True
// only for a still-active (queued|running) job. Terminal / null -> false.
export function shouldResumeJob(job) {
  return !!(job && (job.status === 'queued' || job.status === 'running'));
}

// Watch an existing job id, normalizing each update for the map page. On
// 'complete' it loads the worker-persisted geometry (loadProject) and hands the
// caller a ready {elements,routes,summary}; on 'failed'/'cancelled'/'running'
// it passes the status through. Returns the watchDesignJob handle ({stop}).
export function attachWatch(projectId, jobId, cb) {
  const onJobUpdate = async (j) => {
    if (!j) return;
    if (!TERMINAL.includes(j.status)) {
      cb({ status: j.status, stage: j.stage, stage_detail: j.stage_detail, progress_done: j.progress_done, progress_total: j.progress_total });
      return;
    }
    if (j.status === 'complete') {
      const { elements, routes } = await loadProject(projectId);
      cb({ status: 'complete', result: { elements: elements || [], routes: routes || [], summary: j.result_summary || {} } });
      return;
    }
    if (j.status === 'failed') { cb({ status: 'failed', error: j.error }); return; }
    cb({ status: 'cancelled' });
  };
  return watchDesignJob(jobId, onJobUpdate);
}

// Kick off a fresh run and attach a watcher (seedHints, freshSeedIds: see runDesign). On the 202
// {job_id} returns the watcher handle; on a non-ok run it reports {status:'failed',
// error} via cb and returns null (no watcher to stop).
//
// When the server answers {job_id, already_running:true}, ANOTHER job was
// already in flight for this project and THIS request was NOT enqueued. The cb
// gets a one-off {status:'already_running'} BEFORE any watch update so rerun
// flows can avoid claiming the foreign job's completion as their own success
// (no snapshot, show a notice); we still attach so the UI tracks the active
// job to its terminal state.
export async function runAndWatch(projectId, cb, seedHints, freshSeedIds) {
  const { job_id: jobId, error, already_running: alreadyRunning } = await runDesign(projectId, seedHints, freshSeedIds);
  if (error || !jobId) { cb({ status: 'failed', error: error || 'اجرای طراحی ناموفق بود' }); return null; }
  if (alreadyRunning) cb({ status: 'already_running', job_id: jobId });
  return attachWatch(projectId, jobId, cb);
}

// Resume-on-mount: if the project has an ACTIVE job, re-attach to it and return
// the handle; otherwise return null (nothing in flight).
export async function resumeActive(projectId, cb) {
  const { job } = await getActiveDesignJob(projectId);
  if (!shouldResumeJob(job)) return null;
  return attachWatch(projectId, job.id, cb);
}
